import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/motion";
import { insights, insightCategories } from "@/data/insights";
import { usePageMeta } from "@/lib/api";

export default function InsightsPage() {
  usePageMeta(
    "Insights | SarvaRitam — ESG & Sustainability, Climate, BRSR & Finance",
    "Articles, guides, explainers and FAQs on ESG strategy, carbon, BRSR, climate risk, biodiversity and responsible investment."
  );
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return insights.filter((a) => {
      const inCat = cat === "All" || a.category === cat;
      const q = query.trim().toLowerCase();
      const inQ = !q || (a.title + a.excerpt + a.category).toLowerCase().includes(q);
      return inCat && inQ;
    });
  }, [cat, query]);

  return (
    <>
      <PageHero
        eyebrow="Insights hub"
        title={[<>Thinking, written <span className="serif-it text-gold">plainly.</span></>]}
        intro="Practical writing on ESG, climate, carbon, reporting and responsible investment — built for decision-makers, not for jargon."
      />
      <section className="bg-offwhite text-charcoal sec" data-testid="insights-page">
        <div className="wrap">
          <div className="flex flex-col lg:flex-row gap-6 mb-12">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search insights…"
                className="w-full bg-white/70 border rule-light pl-11 pr-4 py-3.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest"
                data-testid="insights-search-input"
                aria-label="Search insights"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {["All", ...insightCategories].map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 text-[12px] font-bold rounded-full border transition-all ${cat === c ? "bg-forest text-offwhite border-forest" : "border-forest/25 text-forest/70 hover:border-forest"}`}
                  data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-dashed rule-light p-16 text-center" data-testid="insights-empty-state">
              <p className="font-display font-semibold text-xl text-charcoal/70">No insights match your search.</p>
              <p className="text-[13.5px] text-charcoal/50 mt-3">Try a different keyword or clear the category filter.</p>
              <button onClick={() => { setQuery(""); setCat("All"); }} className="btn-forest mt-7" data-testid="insights-clear-filters">Clear filters</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 0.06}>
                  <Link to={`/insights/${a.slug}`} className="lift group flex flex-col h-full border rule-light bg-white/60 hover:bg-white hover:border-forest p-8 rounded-sm" data-testid={`insight-card-${a.slug}`}>
                    <div className="flex items-center justify-between font-mono text-[10.5px] uppercase tracking-widest2 text-sage">
                      <span><span className="text-forest">{a.type}</span> · {a.category}</span>
                      <span>{a.minutes} min</span>
                    </div>
                    <h2 className="font-display font-semibold text-xl mt-5 leading-snug group-hover:text-forest">{a.title}</h2>
                    <p className="text-charcoal/55 text-[14px] mt-4 leading-relaxed line-clamp-3 flex-1">{a.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 mt-6 font-bold text-[12.5px] text-forest">
                      Read insight <ArrowUpRight size={13} />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
